import { NavigateNext as NavigateNextIcon } from '@mui/icons-material'
import { Box, Breadcrumbs as MUIBreadcrumbs, Typography } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { LinkRouter } from '@/helpers/compositions/LinkRouter'
import { useChromeCollapsed } from '@/hooks/useChromeCollapse'
import { useIntlRich } from '@/hooks/useIntlRich'
import { useViewDomRect } from '@/hooks/useViewDomRect'
import { useBreadcrumbsContext } from '@/providers/Global'

//	///////////////////////
//	MARK: TYPES
export interface ICBreadcrumbsPart {
  children?: string
  location?: string
}

export interface ICBreadcrumbsProps {}

//	///////////////////////
//	MARK: EXPORTS
const BreadcrumbsInternal: React.FC<ICBreadcrumbsProps> = (props) => {
  const intl = useIntlRich()
  const location = useLocation()
  const ctx = useBreadcrumbsContext()

  const [ref] = useViewDomRect('breadcrumb')

  const chromeCollapsed = useChromeCollapsed()

  const isDashboard = location.pathname === '/' || location.pathname.startsWith('/dashboard')

  const parts = useMemo<ICBreadcrumbsPart[]>(() => {
    let locationParts = location.pathname.slice(1).split('/')

    const value: ICBreadcrumbsPart[] = [
      {
        children: intl.formatMessageTyped({ id: 'lang.my_dashboard' }),
        location: '/',
      },
    ]

    let previousLocation = ''

    for (const breadcrumb of ctx.values) {
      const children =
        typeof breadcrumb.value.title === 'string' && breadcrumb.value.title.startsWith('id:')
          ? intl.formatMessageUntyped({ id: breadcrumb.value.title.slice(3) })
          : typeof breadcrumb.value.title === 'number'
            ? breadcrumb.value.title.toString()
            : typeof breadcrumb.value.title === 'string'
              ? breadcrumb.value.title
              : undefined

      if (breadcrumb.value.skipLocation) {
        value.push({
          children,
        })
      } else if (breadcrumb.value.location) {
        // Lien explicite : ne consomme aucun segment d'URL, les crumbs suivants
        // gardent donc le `previousLocation` calculé jusqu'ici.
        value.push({
          children,
          location: breadcrumb.value.location,
        })
      } else {
        const currentLocationParts = locationParts.slice(0, breadcrumb.value.span ?? 1)
        locationParts = locationParts.slice(breadcrumb.value.span ?? 1)

        const currentLocation =
          currentLocationParts.length > 0
            ? `${previousLocation}/${currentLocationParts.join('/')}${
                locationParts.length === 0 && location.search ? `?${location.search}` : ''
              }`
            : previousLocation

        value.push({
          children,
          location: currentLocation,
        })

        previousLocation = currentLocation
      }
    }

    return value
  }, [ctx.values, intl, location.pathname, location.search])

  const title = useMemo(
    () =>
      [
        ...[...parts]
          .reverse()
          .map((e) => e.children)
          .filter((e) => !!e),
        intl.formatMessageTyped({ id: 'h.seo.title' }),
      ].join(' | '),
    [intl, parts],
  )

  useEffect(() => {
    document.title = title
  }, [title])

  // Le composant reste monté même replié : c'est lui qui tient le `document.title`.
  if (isDashboard || parts.length <= 1 || chromeCollapsed) {
    return null
  }

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        width: '100%',
        color: 'grey.800',
        mb: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
        }}
      >
        <MUIBreadcrumbs
          aria-label='breadcrumb'
          separator={<NavigateNextIcon fontSize='small' sx={{ color: 'grey.500' }} />}
        >
          {parts.map((e, i) =>
            e.location ? (
              <LinkRouter color='grey.800' key={i} to={e.location}>
                {e.children}
              </LinkRouter>
            ) : (
              <Typography color='grey.800' key={i}>
                {e.children}
              </Typography>
            ),
          )}
        </MUIBreadcrumbs>
      </Box>
    </Box>
  )
}

export const Breadcrumbs = React.memo(BreadcrumbsInternal) as typeof BreadcrumbsInternal
